import { useTranslation } from "@/hooks/useTranslation";
import { useMemo } from "react";
import type { StreamdownTranslations } from "streamdown";

/**
 * Localized labels for Streamdown's built-in UI (external-link dialog, code and
 * table controls, image and diagram actions), which otherwise renders in English.
 * Format names (CSV, TSV, Markdown, SVG, PNG, MMD) keep their defaults.
 * @returns Translations to pass to `Streamdown`'s `translations` prop.
 */
export function useStreamdownTranslations(): Partial<StreamdownTranslations> {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      openExternalLink: t("components.markdown.openExternalLink"),
      externalLinkWarning: t("components.markdown.externalLinkWarning"),
      openLink: t("components.markdown.openLink"),
      copyLink: t("components.markdown.copyLink"),
      close: t("components.markdown.close"),
      copied: t("components.markdown.copied"),
      copyCode: t("components.markdown.copyCode"),
      copyTable: t("components.markdown.copyTable"),
      copyTableAsCsv: t("components.markdown.copyTableAsCsv"),
      copyTableAsTsv: t("components.markdown.copyTableAsTsv"),
      copyTableAsMarkdown: t("components.markdown.copyTableAsMarkdown"),
      downloadFile: t("components.markdown.downloadFile"),
      downloadImage: t("components.markdown.downloadImage"),
      downloadTable: t("components.markdown.downloadTable"),
      downloadTableAsCsv: t("components.markdown.downloadTableAsCsv"),
      downloadTableAsMarkdown: t("components.markdown.downloadTableAsMarkdown"),
      downloadDiagram: t("components.markdown.downloadDiagram"),
      downloadDiagramAsSvg: t("components.markdown.downloadDiagramAsSvg"),
      downloadDiagramAsPng: t("components.markdown.downloadDiagramAsPng"),
      downloadDiagramAsMmd: t("components.markdown.downloadDiagramAsMmd"),
      imageNotAvailable: t("components.markdown.imageNotAvailable"),
      viewFullscreen: t("components.markdown.viewFullscreen"),
      exitFullscreen: t("components.markdown.exitFullscreen"),
    }),
    [t]
  );
}
